import { alpha } from '@mui/material/styles';
import { Box, Grid, Stack, Typography } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useTranslation } from 'react-i18next';

import { PublicBackdrop, PublicContainer, publicPalette } from './PublicLayout';

const AuthShell = ({ children, mode = 'account' }) => {
  const { t } = useTranslation();
  const isSignup = mode === 'signup' || mode === 'activate';

  return (
    <PublicBackdrop sx={{ minHeight: 'calc(100vh - 76px)', display: 'flex', alignItems: 'center', py: { xs: 4, md: 7 } }}>
      <PublicContainer sx={{ maxWidth: 1160 }}>
        <Grid
          container
          sx={{
            overflow: 'hidden',
            border: `1px solid ${publicPalette.border}`,
            borderRadius: { xs: 3, md: 5 },
            backgroundColor: '#FFFFFF',
            boxShadow: '0 40px 120px rgba(0,0,0,.46)',
          }}
        >
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'relative',
              minHeight: 610,
              p: 6,
              color: publicPalette.text,
              background: `radial-gradient(circle at 20% 10%, ${alpha(publicPalette.primary, 0.24)}, transparent 35%), radial-gradient(circle at 85% 75%, ${alpha(publicPalette.secondary, 0.25)}, transparent 38%), #07101F`,
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                opacity: 0.35,
                backgroundImage: `linear-gradient(${alpha('#FFFFFF', 0.08)} 1px, transparent 1px), linear-gradient(90deg, ${alpha('#FFFFFF', 0.08)} 1px, transparent 1px)`,
                backgroundSize: '42px 42px',
                pointerEvents: 'none',
              },
            }}
          >
            <Stack spacing={3} justifyContent="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ width: 54, height: 54, display: 'grid', placeItems: 'center', borderRadius: 3, color: publicPalette.primary, border: `1px solid ${alpha(publicPalette.primary, 0.35)}`, backgroundColor: alpha(publicPalette.primary, 0.1) }}>
                {isSignup ? <AutoAwesomeRoundedIcon /> : <LockRoundedIcon />}
              </Box>
              <Typography component="h2" sx={{ fontSize: 42, lineHeight: 1.08, fontWeight: 900, letterSpacing: '-.045em' }}>
                {isSignup
                  ? t('public.auth_signup_title', { defaultValue: 'Start a cleaner invoicing workflow.' })
                  : t('public.auth_welcome_title', { defaultValue: 'Your document workspace is ready.' })}
              </Typography>
              <Typography sx={{ color: publicPalette.muted, fontSize: 17, lineHeight: 1.75 }}>
                {isSignup
                  ? t('public.auth_signup_text', { defaultValue: 'Create the account, configure your company, and send your first professional document in minutes.' })
                  : t('public.auth_welcome_text', { defaultValue: 'Securely access invoices, quotes, customers, and shared company data from one focused workspace.' })}
              </Typography>
              <Stack spacing={1.4} sx={{ pt: 1 }}>
                {[
                  t('public.auth_benefit_1', { defaultValue: 'Cloud access from any device' }),
                  t('public.auth_benefit_2', { defaultValue: 'Invoices and quotes in one place' }),
                  t('public.auth_benefit_3', { defaultValue: 'A free plan to get started' }),
                ].map((item) => (
                  <Stack key={item} direction="row" spacing={1.2} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ color: publicPalette.accent, fontSize: 19 }} />
                    <Typography sx={{ fontWeight: 700 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7} sx={{ backgroundColor: '#FFFFFF' }}>
            <Box
              sx={{
                width: '100%',
                maxWidth: 620,
                mx: 'auto',
                p: { xs: 2.5, sm: 5, md: 6 },
                color: '#0F172A',
                '& > .MuiButton-root:first-of-type': { mb: 2 },
                '& .MuiTypography-h5': { mt: '24px !important', mb: '28px !important', fontSize: { xs: 28, sm: 34 }, fontWeight: 900, letterSpacing: '-.035em', color: '#0F172A' },
                '& .MuiTextField-root': { backgroundColor: '#FFFFFF' },
                '& .MuiButton-containedPrimary': { minHeight: 48, borderRadius: 2.5, textTransform: 'none', fontWeight: 900, background: `linear-gradient(110deg, #0EA5E9, #14B8A6)` },
                '& .MuiButton-textPrimary': { textTransform: 'none', fontWeight: 750 },
              }}
            >
              {children}
            </Box>
          </Grid>
        </Grid>
      </PublicContainer>
    </PublicBackdrop>
  );
};

export default AuthShell;
